import { useState, type FormEvent, type ReactNode } from "react";
import type { JobContract } from "../api/types.js";
import { Async, Failure } from "../components/Async.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";

/**
 * What was agreed, so a contract can be produced from it.
 *
 * WHY THIS SCREEN EXISTS. `job_contracts`, `upsertJobContract` and
 * `PUT /api/jobs/:jobId/contract` have existed since migration 0023 with
 * nothing in the portal calling any of them, so the only way to record what a
 * customer had agreed was to write it into the database by hand. The
 * consequence was not subtle: `doc-templates/contract.ts` refuses to render
 * without these terms, so Generate always failed and the reason was invisible.
 *
 * THE VENUE IS THE FIELD TO UNDERSTAND. Where the contract was agreed decides
 * the customer's cancellation rights under the Consumer Contracts
 * (Information, Cancellation and Additional Charges) Regulations 2013 — away
 * from your premises or at a distance carries a 14-day right to cancel, on
 * your own premises does not. The template refuses to render without it rather
 * than guess, because a contract carrying the wrong cancellation terms is
 * worse than no contract. There is deliberately no default in this form for
 * the same reason.
 *
 * MONEY IS IN POUNDS HERE AND PENCE EVERYWHERE ELSE. The conversion happens at
 * this boundary and nowhere else. Nobody types 750000 meaning £7,500, and
 * carrying pounds any further in would eventually put 7499.999999 on a legal
 * document.
 */

type Draft = {
  reference: string;
  venue: string;
  agreedOn: string;
  consumerCodeName: string;
  consumerCodeMembership: string;
  consumerCodeDispute: string;
  workDescription: string;
  heatPump: string;
  cylinder: string;
  emitterWorks: string;
  controls: string;
  expectedStart: string;
  expectedDurationWorkingDays: string;
  workmanshipYears: string;
  workmanshipBackedBy: string;
  manufacturerWarranty: string;
  maintenanceRequirement: string;
  earlyStartRequested: string;
  returnCostsBorneBy: string;
  performanceEstimateProvided: boolean;
  performanceEstimateReference: string;
  performanceEstimateProvidedOn: string;
  grantScheme: string;
  grantAmount: string;
  grantStatus: string;
  grantIfNotPaid: string;
  depositAmount: string;
  lines: { description: string; quantity: string; unit: string; unitPrice: string; vatPercent: string }[];
  stages: { label: string; when: string; amount: string }[];
};

const BLANK_LINE = { description: "", quantity: "1", unit: "job", unitPrice: "", vatPercent: "0" };
const BLANK_STAGE = { label: "", when: "", amount: "" };

/** Pounds as typed → whole pence. Rounded, never truncated. */
function pence(pounds: string): number {
  const n = Number(pounds.replace(/[£,\s]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
function pounds(p: number | undefined): string {
  return p === undefined ? "" : (p / 100).toFixed(2);
}

function toDraft(c: JobContract | null): Draft {
  return {
    reference: c?.reference ?? "",
    venue: c?.venue ?? "",
    agreedOn: c?.agreedOn ?? "",
    consumerCodeName: c?.consumerCode?.name ?? "",
    consumerCodeMembership: c?.consumerCode?.membershipNumber ?? "",
    consumerCodeDispute: c?.consumerCode?.disputeResolution ?? "",
    workDescription: c?.workDescription ?? "",
    heatPump: c?.system?.heatPump ?? "",
    cylinder: c?.system?.cylinder ?? "",
    emitterWorks: c?.system?.emitterWorks ?? "",
    controls: c?.system?.controls ?? "",
    expectedStart: c?.timescale?.expectedStart ?? "",
    expectedDurationWorkingDays: c?.timescale?.expectedDurationWorkingDays?.toString() ?? "",
    workmanshipYears: c?.guarantee?.workmanshipYears?.toString() ?? "",
    workmanshipBackedBy: c?.guarantee?.workmanshipBackedBy ?? "",
    manufacturerWarranty: c?.guarantee?.manufacturerWarranty ?? "",
    maintenanceRequirement: c?.guarantee?.maintenanceRequirement ?? "",
    // "" rather than false: the template demands a real boolean, and an
    // unanswered question must not arrive as "no".
    earlyStartRequested:
      c?.cancellation?.earlyStartRequested === undefined
        ? ""
        : String(c.cancellation.earlyStartRequested),
    returnCostsBorneBy: c?.cancellation?.returnCostsBorneBy ?? "",
    performanceEstimateProvided: c?.performanceEstimate?.provided ?? false,
    performanceEstimateReference: c?.performanceEstimate?.documentReference ?? "",
    performanceEstimateProvidedOn: c?.performanceEstimate?.providedOn ?? "",
    grantScheme: c?.grant?.scheme ?? "",
    grantAmount: pounds(c?.grant?.amountPence),
    grantStatus: c?.grant?.status ?? "",
    grantIfNotPaid: c?.grant?.ifNotPaid ?? "",
    depositAmount: pounds(c?.depositPence),
    lines:
      c?.lines && c.lines.length > 0
        ? c.lines.map((l) => ({
            description: l.description,
            quantity: String(l.quantity),
            unit: l.unit ?? "",
            unitPrice: pounds(l.unitPricePence),
            vatPercent: (l.vatRateBasisPoints / 100).toString(),
          }))
        : [{ ...BLANK_LINE }],
    stages:
      c?.paymentStages && c.paymentStages.length > 0
        ? c.paymentStages.map((s) => ({
            label: s.label,
            when: s.when,
            amount: pounds(s.amountPence),
          }))
        : [{ ...BLANK_STAGE }],
  };
}

function toPayload(d: Draft): unknown {
  const some = (v: string) => (v.trim() === "" ? undefined : v.trim());
  const whole = (v: string) => {
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : undefined;
  };
  return {
    reference: some(d.reference),
    venue: some(d.venue),
    agreedOn: some(d.agreedOn),
    consumerCode:
      some(d.consumerCodeName) && some(d.consumerCodeMembership)
        ? {
            name: some(d.consumerCodeName),
            membershipNumber: some(d.consumerCodeMembership),
            disputeResolution: some(d.consumerCodeDispute),
          }
        : undefined,
    workDescription: some(d.workDescription),
    system: {
      heatPump: some(d.heatPump),
      cylinder: some(d.cylinder),
      emitterWorks: some(d.emitterWorks),
      controls: some(d.controls),
    },
    // EACH GROUP BELOW IS ALL-OR-NOTHING, and the API is right to insist.
    // A timescale with a start and no duration, or a guarantee with a length
    // and no warranty, is not a half-filled form — it is a term that would
    // print onto a contract saying less than it appears to. So a group is
    // sent only once the parts that make it mean something are there, and
    // omitted entirely until then.
    timescale:
      some(d.expectedStart) && whole(d.expectedDurationWorkingDays)
        ? {
            expectedStart: some(d.expectedStart),
            expectedDurationWorkingDays: whole(d.expectedDurationWorkingDays),
          }
        : undefined,
    guarantee:
      whole(d.workmanshipYears) && some(d.manufacturerWarranty)
        ? {
            workmanshipYears: whole(d.workmanshipYears),
            workmanshipBackedBy: some(d.workmanshipBackedBy),
            manufacturerWarranty: some(d.manufacturerWarranty),
            maintenanceRequirement: some(d.maintenanceRequirement),
          }
        : undefined,
    // Only sent once answered. The API demands a real boolean and refuses
    // anything else, which is what keeps "not asked" out of a contract.
    cancellation:
      d.earlyStartRequested === ""
        ? undefined
        : {
            earlyStartRequested: d.earlyStartRequested === "true",
            returnCostsBorneBy: some(d.returnCostsBorneBy),
          },
    depositPence: d.depositAmount.trim() === "" ? undefined : pence(d.depositAmount),
    // provided: false is a complete answer on its own — the reference and the
    // date only exist once there is an estimate to refer to.
    performanceEstimate: d.performanceEstimateProvided
      ? {
          provided: true,
          documentReference: some(d.performanceEstimateReference),
          providedOn: some(d.performanceEstimateProvidedOn),
        }
      : { provided: false },
    grant:
      some(d.grantScheme) &&
      d.grantAmount.trim() !== "" &&
      some(d.grantStatus) &&
      some(d.grantIfNotPaid)
        ? {
            scheme: some(d.grantScheme),
            amountPence: pence(d.grantAmount),
            status: some(d.grantStatus),
            ifNotPaid: some(d.grantIfNotPaid),
          }
        : undefined,
    lines: d.lines
      .filter((l) => l.description.trim() !== "")
      .map((l) => ({
        description: l.description.trim(),
        quantity: Number(l.quantity) || 1,
        unit: some(l.unit),
        unitPricePence: pence(l.unitPrice),
        vatRateBasisPoints: Math.round((Number(l.vatPercent) || 0) * 100),
      })),
    paymentStages: d.stages
      .filter((s) => s.label.trim() !== "")
      .map((s) => ({
        label: s.label.trim(),
        when: s.when.trim(),
        amountPence: pence(s.amount),
      })),
  };
}

export function JobContractScreen({ jobId }: { readonly jobId: string }): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<JobContract | null>(
    () => client.jobContract(jobId),
    [client, jobId],
  );

  return (
    <section>
      <div className="page-header">
        <p className="small">
          <a href={href.job(jobId)}>Back to the job</a>{" "}
          <span className="muted">·</span>{" "}
          <a href={href.jobDocuments(jobId)}>Documents</a>
        </p>
        <h1>What was agreed</h1>
        <p className="lede">
          The terms a contract is produced from. The agreement itself is
          generated on the Documents screen.
        </p>
      </div>
      <Async state={state} what="this job's contract terms" reload={reload}>
        {(contract) => <ContractForm jobId={jobId} contract={contract} />}
      </Async>
    </section>
  );
}

function ContractForm({
  jobId,
  contract,
}: {
  readonly jobId: string;
  readonly contract: JobContract | null;
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [d, setD] = useState<Draft>(() => toDraft(contract));
  const [saved, setSaved] = useState(false);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => {
    setSaved(false);
    setD((prev) => ({ ...prev, [key]: value }));
  };
  const text = (key: keyof Draft) => ({
    value: String(d[key] ?? ""),
    onChange: (e: { target: { value: string } }) => set(key, e.target.value as Draft[typeof key]),
  });

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    await submit(async () => {
      await client.saveJobContract(jobId, toPayload(d));
      setSaved(true);
    });
  };

  return (
    <form className="sectioned" onSubmit={(e) => void onSubmit(e)}>
      {error !== null ? <Failure error={error} what="saving these terms" /> : null}

      {d.venue === "" ? (
        <div className="notice warn" role="alert">
          <p>
            A contract cannot be produced until you say <strong>where it was
            agreed</strong>. That decides the customer&rsquo;s right to cancel,
            and it is not something the system may guess.
          </p>
        </div>
      ) : null}

      <h2>The agreement</h2>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-ref">Contract reference</label>
          <input id="c-ref" type="text" {...text("reference")} />
        </div>
        <div className="field">
          <label htmlFor="c-venue">Where it was agreed</label>
          <select id="c-venue" {...text("venue")}>
            <option value="">Not answered</option>
            <option value="off-premises">Away from our premises — at the customer&rsquo;s home</option>
            <option value="distance">At a distance — phone, post or online</option>
            <option value="on-premises">At our own premises</option>
          </select>
          <p className="hint small">
            The first two carry a 14-day right to cancel. The third does not.
          </p>
        </div>
        <div className="field">
          <label htmlFor="c-agreed">Date agreed</label>
          <input id="c-agreed" type="date" {...text("agreedOn")} />
        </div>
        <div className="field">
          <label htmlFor="c-deposit">Deposit (£)</label>
          <input id="c-deposit" type="text" inputMode="decimal" {...text("depositAmount")} />
        </div>
      </div>

      <h2>Consumer code</h2>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-cc-name">Scheme</label>
          <input id="c-cc-name" type="text" {...text("consumerCodeName")} />
        </div>
        <div className="field">
          <label htmlFor="c-cc-no">Membership number</label>
          <input id="c-cc-no" type="text" {...text("consumerCodeMembership")} />
        </div>
        <div className="field">
          <label htmlFor="c-cc-adr">Dispute resolution</label>
          <input id="c-cc-adr" type="text" {...text("consumerCodeDispute")} />
        </div>
      </div>

      <h2>The work</h2>
      <div className="field">
        <label htmlFor="c-work">What is being done</label>
        <textarea id="c-work" rows={3} {...text("workDescription")} />
      </div>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-hp">Heat pump</label>
          <input id="c-hp" type="text" {...text("heatPump")} />
        </div>
        <div className="field">
          <label htmlFor="c-cyl">Hot water cylinder</label>
          <input id="c-cyl" type="text" {...text("cylinder")} />
        </div>
        <div className="field">
          <label htmlFor="c-emit">Emitters</label>
          <input id="c-emit" type="text" {...text("emitterWorks")} />
        </div>
        <div className="field">
          <label htmlFor="c-ctrl">Controls</label>
          <input id="c-ctrl" type="text" {...text("controls")} />
        </div>
      </div>

      <h2>Price</h2>
      <Rows
        rows={d.lines}
        blank={BLANK_LINE}
        onChange={(lines) => set("lines", lines)}
        addLabel="Add a line"
        render={(row, i, update) => (
          <div className="fields" key={i}>
            <div className="field">
              <label htmlFor={`c-l-desc-${i}`}>Description</label>
              <input
                id={`c-l-desc-${i}`}
                type="text"
                value={row.description}
                onChange={(e) => update({ ...row, description: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`c-l-qty-${i}`}>Quantity</label>
              <input
                id={`c-l-qty-${i}`}
                type="text"
                inputMode="decimal"
                value={row.quantity}
                onChange={(e) => update({ ...row, quantity: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`c-l-price-${i}`}>Unit price (£)</label>
              <input
                id={`c-l-price-${i}`}
                type="text"
                inputMode="decimal"
                value={row.unitPrice}
                onChange={(e) => update({ ...row, unitPrice: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`c-l-vat-${i}`}>VAT %</label>
              <input
                id={`c-l-vat-${i}`}
                type="text"
                inputMode="decimal"
                value={row.vatPercent}
                onChange={(e) => update({ ...row, vatPercent: e.target.value })}
              />
            </div>
          </div>
        )}
      />

      <h2>Payment</h2>
      <Rows
        rows={d.stages}
        blank={BLANK_STAGE}
        onChange={(stages) => set("stages", stages)}
        addLabel="Add a payment stage"
        render={(row, i, update) => (
          <div className="fields" key={i}>
            <div className="field">
              <label htmlFor={`c-s-label-${i}`}>Stage</label>
              <input
                id={`c-s-label-${i}`}
                type="text"
                value={row.label}
                onChange={(e) => update({ ...row, label: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`c-s-when-${i}`}>When it falls due</label>
              <input
                id={`c-s-when-${i}`}
                type="text"
                value={row.when}
                onChange={(e) => update({ ...row, when: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`c-s-amt-${i}`}>Amount (£)</label>
              <input
                id={`c-s-amt-${i}`}
                type="text"
                inputMode="decimal"
                value={row.amount}
                onChange={(e) => update({ ...row, amount: e.target.value })}
              />
            </div>
          </div>
        )}
      />

      <h2>Timetable</h2>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-start">Expected start</label>
          <input id="c-start" type="text" {...text("expectedStart")} />
          <p className="hint small">In words — a firm date usually cannot be given at signing.</p>
        </div>
        <div className="field">
          <label htmlFor="c-days">Expected duration (working days)</label>
          <input id="c-days" type="text" inputMode="numeric" {...text("expectedDurationWorkingDays")} />
        </div>
      </div>

      <h2>Guarantees</h2>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-wy">Workmanship guarantee (years)</label>
          <input id="c-wy" type="text" inputMode="numeric" {...text("workmanshipYears")} />
        </div>
        <div className="field">
          <label htmlFor="c-wb">Backed by</label>
          <input id="c-wb" type="text" {...text("workmanshipBackedBy")} />
        </div>
        <div className="field">
          <label htmlFor="c-mw">Manufacturer warranty</label>
          <input id="c-mw" type="text" {...text("manufacturerWarranty")} />
        </div>
        <div className="field">
          <label htmlFor="c-mr">Maintenance requirement</label>
          <input id="c-mr" type="text" {...text("maintenanceRequirement")} />
        </div>
      </div>

      <h2>Cancellation</h2>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-early">Did the customer ask us to start within the cancellation period?</label>
          <select id="c-early" {...text("earlyStartRequested")}>
            <option value="">Not answered</option>
            <option value="false">No — work begins after the 14 days</option>
            <option value="true">Yes — they asked in writing for an earlier start</option>
          </select>
          <p className="hint small">
            Regulation 36 makes this an express request. It must be asked, not assumed.
          </p>
        </div>
        <div className="field">
          <label htmlFor="c-return">Who pays to return goods on cancellation</label>
          <select id="c-return" {...text("returnCostsBorneBy")}>
            <option value="">Not answered</option>
            <option value="contractor">We do</option>
            <option value="customer">The customer does</option>
          </select>
        </div>
      </div>

      <h2>Performance estimate</h2>
      <label className="field checkbox">
        <input
          type="checkbox"
          checked={d.performanceEstimateProvided}
          onChange={(e) => set("performanceEstimateProvided", e.target.checked)}
        />
        <span>
          The customer was given the Pre-Sale Information and System Performance
          Estimate before signing. MCS requires this, and the contract says so.
        </span>
      </label>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-pe-ref">Its reference</label>
          <input id="c-pe-ref" type="text" {...text("performanceEstimateReference")} />
        </div>
        <div className="field">
          <label htmlFor="c-pe-on">Date provided</label>
          <input id="c-pe-on" type="date" {...text("performanceEstimateProvidedOn")} />
        </div>
      </div>

      <h2>Grant</h2>
      <div className="fields">
        <div className="field">
          <label htmlFor="c-gs">Scheme</label>
          <input id="c-gs" type="text" {...text("grantScheme")} />
        </div>
        <div className="field">
          <label htmlFor="c-ga">Grant amount (£)</label>
          <input id="c-ga" type="text" inputMode="decimal" {...text("grantAmount")} />
        </div>
        <div className="field">
          <label htmlFor="c-gst">Status</label>
          <select id="c-gst" {...text("grantStatus")}>
            <option value="">Not answered</option>
            <option value="not-yet-applied">Not yet applied for</option>
            <option value="applied">Applied for</option>
            <option value="voucher-issued">Voucher issued</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="c-gi">If the grant is not paid</label>
          <input id="c-gi" type="text" {...text("grantIfNotPaid")} />
        </div>
      </div>

      <p>
        <button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save what was agreed"}
        </button>
        {saved ? <span className="small muted"> Saved.</span> : null}
      </p>
    </form>
  );
}

/** A repeatable group of fields — priced lines, and payment stages. */
function Rows<T>({
  rows,
  blank,
  onChange,
  render,
  addLabel,
}: {
  readonly rows: T[];
  readonly blank: T;
  readonly onChange: (rows: T[]) => void;
  readonly render: (row: T, index: number, update: (next: T) => void) => ReactNode;
  readonly addLabel: string;
}): ReactNode {
  return (
    <>
      {rows.map((row, i) => (
        <div key={i}>
          {render(row, i, (next) => onChange(rows.map((r, j) => (j === i ? next : r))))}
          {rows.length > 1 ? (
            <p>
              <button
                type="button"
                className="link"
                onClick={() => onChange(rows.filter((_, j) => j !== i))}
              >
                Remove
              </button>
            </p>
          ) : null}
        </div>
      ))}
      <p>
        <button type="button" className="secondary" onClick={() => onChange([...rows, { ...blank }])}>
          {addLabel}
        </button>
      </p>
    </>
  );
}
